/**
 * WoUploadModal — step 1 of the work-order sample upload (2026-09-10): pick a
 * file in ANY of the three formats the lab has samples in, review it, hand the
 * normalized rows to the parent (which reconciles them against the work order).
 *
 *   - Flat CSV (our WO template) — parsed + validated in the browser
 *     (woUploadParser, unchanged), previewed row by row.
 *   The two are told apart by file CONTENT (zip / OLE signature), not by
 *   extension — the lab's KeyLAB workbooks sometimes arrive named .csv.
 *   - MoftakiLab intake workbook (.xlsx) or a filled legacy KeyLAB request form
 *     (.xlsx/.xlsm) — analyzed server-side by the SAME module the client intake
 *     portal uses, then confirmed in the shared ImportReviewDialog (row
 *     include-boxes + KeyLAB test-column mapping). Project-level fields in the
 *     workbook are shown there read-only and never touch the project.
 *
 * Emits `WoSampleUploadRowInput[]` either way — the parent owns what happens
 * next (reconcile → WoSampleReconcileDialog → apply).
 */
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { CheckCircle, Close, CloudUpload, Download, ErrorOutline, WarningAmber } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';
import { parseCsv } from '../../utils/csvParser';
import {
  type WoCsvRowValidation, type WoFileValidationError, WO_LOCATION_ID_LABEL,
  applyWoHeaderAliases, checkWoInFileDuplicates, downloadWoCsvTemplate, validateWoFile,
  validateWoHeaders, validateWoRow, validateWoRowCount,
} from '../../utils/woUploadParser';
import { csvRowsToUploadRows, intakeSamplesToUploadRows } from '../../utils/woSampleUploadRows';
import { ImportReviewDialog } from '../../components/intakeImport/ImportReviewDialog';
import { analyzeWorkOrderSampleWorkbook } from '../../services/workOrderSampleUploadService';
import { apiErrorText } from '../../utils/apiErrorText';
import { detectUploadFileKind, readFileHead } from '../../utils/uploadFileKind';
import type { WoSampleUploadRowInput, WoSampleWorkbookAnalysis } from '../../types/workOrderSampleUpload';
import type { PublicIntakeSample } from '../../types/intake';

interface WoUploadModalProps {
  open: boolean;
  onClose: () => void;
  /** The normalized rows; the parent reconciles + applies them. Throw to keep the modal open. */
  onImport: (rows: WoSampleUploadRowInput[]) => Promise<void>;
  /** True when the rows land on an existing work order (copy mentions placeholders). */
  hasWorkOrder: boolean;
}

export const WoUploadModal = ({ open, onClose, onImport, hasWorkOrder }: WoUploadModalProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<WoFileValidationError | null>(null);
  const [validations, setValidations] = useState<WoCsvRowValidation[]>([]);
  const [fileName, setFileName] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<WoSampleWorkbookAnalysis | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  const validCount = validations.filter((v) => v.valid).length;
  const errorCount = validations.filter((v) => !v.valid).length;
  const warningCount = validations.filter((v) => v.valid && v.errors.some((e) => e.severity === 'warning')).length;
  // FEAT-2026-0019: a valid line can list several tests for one sample.
  const importableTestCount = validations.reduce(
    (sum, v) => sum + (v.valid && v.normalizedRows ? v.normalizedRows.length : 0), 0,
  );
  const hasCsvData = validations.length > 0;

  const reset = () => {
    setFileError(null);
    setValidations([]);
    setFileName('');
    setImportError('');
    setAnalysis(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCsv = useCallback((file: File) => {
    const fileLevelErr = validateWoFile(file);
    if (fileLevelErr) { setFileError(fileLevelErr); setValidations([]); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows, rawRowNumbers } = parseCsv(text);
      const headerErr = validateWoHeaders(headers);
      if (headerErr) { setFileError(headerErr); setValidations([]); return; }
      const rowCountErr = validateWoRowCount(rows.length);
      if (rowCountErr) { setFileError(rowCountErr); setValidations([]); return; }
      setFileError(null);
      applyWoHeaderAliases(rows);
      const results = rows.map((row, i) => validateWoRow(row, rawRowNumbers[i]));
      checkWoInFileDuplicates(results);
      setValidations(results);
    };
    reader.readAsText(file);
  }, []);

  const handleWorkbook = useCallback(async (file: File) => {
    setAnalyzing(true);
    try {
      setAnalysis(await analyzeWorkOrderSampleWorkbook(file));
    } catch (err) {
      setFileError({ message: apiErrorText(err, 'Could not read this workbook.') });
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    setImportError('');
    setFileError(null);
    setValidations([]);
    setAnalysis(null);
    setFileName(file.name);
    // Content first: a KeyLAB workbook saved as ".csv" must still reach the
    // workbook importer (uploadFileKind.ts).
    const kind = detectUploadFileKind(file.name, await readFileHead(file));
    if (kind === 'csv') { handleCsv(file); return; }
    if (kind === 'workbook') { void handleWorkbook(file); return; }
    setFileError({ message: 'Unsupported file type. Upload a .csv (our work-order template), a MoftakiLab intake workbook (.xlsx), or a filled KeyLAB request form (.xlsx/.xlsm).' });
  }, [handleCsv, handleWorkbook]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void handleFileSelect(file);
  }, [handleFileSelect]);

  const submitRows = async (rows: WoSampleUploadRowInput[]) => {
    if (rows.length === 0) return;
    setImporting(true);
    setImportError('');
    try {
      await onImport(rows);
      handleClose();
    } catch (err: unknown) {
      setImportError(apiErrorText(err, 'Upload failed. Please check your connection and try again.'));
    } finally {
      setImporting(false);
    }
  };

  const handleImportCsv = () => {
    const validRows = validations.filter((v) => v.valid && v.normalizedRows).flatMap((v) => v.normalizedRows!);
    void submitRows(csvRowsToUploadRows(validRows));
  };

  const handleWorkbookConfirmed = (result: { samples: PublicIntakeSample[] }) => {
    setAnalysis(null);
    void submitRows(intakeSamplesToUploadRows(result.samples));
  };

  // Keys are the parsed row keys; only the borehole column carries a distinct
  // label (FEAT-2026-0017: "Location ID", the row key stays `Location`).
  const previewCols: { key: string; label: string }[] = [
    'Sample Reference', 'Specimen Ref', 'Specimen Depth', 'Location', 'Depth Top', 'Depth Base',
    'Type', 'Test Name', 'Priority', 'Start Date', 'Instructions',
  ].map((key) => ({ key, label: key === 'Location' ? WO_LOCATION_ID_LABEL : key }));

  return (
    <>
      <Dialog open={open && !analysis} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          Upload Samples &amp; Tests
          <IconButton size="small" onClick={handleClose}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Three formats are accepted: our <b>work-order CSV</b> (one line per sample &amp; test), a
            <b> MoftakiLab intake workbook</b> (.xlsx), or a filled <b>KeyLAB test request form</b> (.xlsx/.xlsm).
            {hasWorkOrder && (
              <> Placeholder rows (&ldquo;Samples pending receipt&rdquo;) are replaced by the uploaded samples;
              you review the match before anything is saved.</>
            )}
          </Typography>
          <Button startIcon={<Download />} onClick={downloadWoCsvTemplate} size="small"
            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}>
            Download CSV Template
          </Button>
          {/* FEAT-2026-0019: multiple tests per sample on one line */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            CSV tip: to request several tests for one sample, list them in the Test Name cell separated
            by <b>;</b> (e.g. <code>Atterberg Limits; Particle Size Distribution</code>). <b>Location ID</b> is the
            sample&rsquo;s borehole / test pit id — an id the project doesn&rsquo;t have yet is created automatically.
          </Typography>

          {/* Drop zone */}
          <Box
            onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`, borderRadius: 2, py: 4, textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.2s ease', backgroundColor: alpha(theme.palette.primary.main, 0.02),
              '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05) },
            }}
          >
            <CloudUpload sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.4), mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {analyzing ? `Reading ${fileName}…` : fileName || 'Drag and drop a .csv / .xlsx / .xlsm file here, or click to browse'}
            </Typography>
            <input
              ref={fileInputRef} type="file" hidden
              accept=".csv,.xlsx,.xlsm,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) void handleFileSelect(file); }}
            />
          </Box>

          {fileError && <Alert severity="error">{fileError.message}</Alert>}
          {importError && <Alert severity="error">{importError}</Alert>}

          {/* CSV summary + preview */}
          {hasCsvData && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip icon={<CheckCircle />} label={`${validCount} valid`} color="success" size="small" variant="outlined" />
                {warningCount > 0 && <Chip icon={<WarningAmber />} label={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`} color="warning" size="small" variant="outlined" />}
                {errorCount > 0 && <Chip icon={<ErrorOutline />} label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`} color="error" size="small" variant="outlined" />}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {validCount} of {validations.length} rows ready
                  {importableTestCount > validCount && ` · ${importableTestCount} tests total`}
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 300, border: `1px solid ${alpha(theme.border.color, 0.5)}`, borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { backgroundColor: theme.table.headerBg, fontWeight: 600, fontSize: '0.75rem' } }}>
                      <TableCell sx={{ width: 36 }}>#</TableCell>
                      {previewCols.map((col) => <TableCell key={col.key}>{col.label}</TableCell>)}
                      <TableCell sx={{ width: 36 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validations.map((v, i) => (
                      <TableRow key={i} sx={{
                        backgroundColor: !v.valid ? alpha(theme.palette.error.main, 0.05)
                          : v.errors.length > 0 ? alpha(theme.palette.warning.main, 0.06) : undefined,
                      }}>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{v.rowNumber}</TableCell>
                        {previewCols.map((col) => {
                          const hasWarning = v.errors.some((e) => e.severity === 'warning' && e.field === col.key);
                          return (
                            <TableCell key={col.key} sx={{ fontSize: '0.8rem', ...(hasWarning && { color: 'warning.dark', fontWeight: 600 }) }}>
                              {v.row[col.key] ?? ''}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          {!v.valid ? (
                            <Tooltip title={v.errors.filter((e) => e.severity === 'error').map((e) => e.message).join('\n')}>
                              <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />
                            </Tooltip>
                          ) : v.errors.length > 0 ? (
                            <Tooltip title={v.errors.map((e) => e.message).join('\n')}>
                              <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
                            </Tooltip>
                          ) : <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disabled={validCount === 0 || importing || analyzing} onClick={handleImportCsv}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          >
            {importing ? 'Matching…'
              : `${hasWorkOrder ? 'Review Match' : 'Import'} (${importableTestCount > validCount ? `${importableTestCount} tests` : validCount})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workbook review — the same confirmation the client intake portal uses. */}
      <ImportReviewDialog
        open={open && analysis !== null}
        preview={analysis?.preview ?? null}
        def={analysis?.formDef ?? { instructionsBlurb: '', checklistItems: [], categories: [], sampleTypeOptions: [], advisoryHints: [] }}
        onCancel={() => setAnalysis(null)}
        onConfirm={handleWorkbookConfirmed}
        confirmLabel={(n) => `${hasWorkOrder ? 'Review match for' : 'Import'} ${n} row${n === 1 ? '' : 's'}`}
        contextNote="Project fields from the workbook are shown for reference only — this upload adds samples to the work order and never changes the project."
      />
    </>
  );
};
